import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  TablePagination,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { format } from 'date-fns'; // For formatting timestamps

const ActivityLogViewer = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0); // 0-based index for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogsCount, setTotalLogsCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // New state for triggering refresh

  // Filter state
  const [filters, setFilters] = useState({
    userId: '',
    eventType: '',
    actorId: '',
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState([]); // For dropdown filters

  // Fetch users for filter dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      if (token) {
        try {
          const userData = await apiService.getAllUsers(token);
          setUsers(userData || []);
        } catch (err) {
          console.error("Failed to fetch users for filter:", err);
        }
      }
    };
    fetchUsers();
  }, [token]);

  // Fetch logs when page, rowsPerPage, or filters change
  useEffect(() => {
    const fetchLogs = async () => {
      if (!token) return;
      setIsLoading(true);
      setError(null);
      try {
        const apiPage = page + 1; // API expects 1-based page
        const activeFilters = {};
        for (const key in filters) {
          if (filters[key]) {
            activeFilters[key] = filters[key];
          }
        }
        const data = await apiService.getAdminActivityLog(token, apiPage, rowsPerPage, activeFilters);

        if (data && Array.isArray(data.events)) {
          setLogs(data.events);
          setTotalLogsCount(data.totalEvents || 0);
        } else {
          setLogs([]);
          setTotalLogsCount(0);
          setError('Received unexpected data format from server.');
        }
      } catch (err) {
        setLogs([]);
        setTotalLogsCount(0);
        setError(err.message || 'Failed to fetch activity logs.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLogs();
  }, [token, page, rowsPerPage, filters, refreshTrigger]); // Add refreshTrigger to dependencies

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleApplyFilters = () => {
    // useEffect will trigger fetchLogs due to filters state change
    // This button is more for explicit user action if needed, 
    // but useEffect already handles it.
    // Forcing a refetch can be done by slightly altering filters if needed,
    // or by having a dedicated "refresh" button.
    // For now, changing a filter value is enough.
    console.log("Applying filters:", filters);
  };
  
  const handleClearFilters = () => {
    setFilters({ userId: '', eventType: '', actorId: '', startDate: '', endDate: '' });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const eventTypes = [
    'USER_LOGIN', 'USER_LOGIN_FAILURE', 'USER_REGISTERED', 'USER_DELETED', 'USER_LOGOUT',
    'MESSAGE_SENT', 'REVIEW_REQUESTED', 'GOAL_CREATED', 'GOAL_UPDATED', 'GOAL_DELETED',
    'JOURNAL_CREATED', 'JOURNAL_UPDATED', 'JOURNAL_DELETED', 'THERAPIST_TAKE_CONTROL',
    'THERAPIST_HANDBACK_TO_AI', 'THERAPIST_ANNOTATED_MESSAGE', 'SESSION_STARTED', 'SESSION_ENDED',
    'PHASE_CHANGED', 'PATHWAY_ASSIGNED', 'USER_ROLE_CHANGED', 'CLIENT_ASSIGNED_TO_THERAPIST',
    'CLIENT_SETTINGS_UPDATED', 'PLATFORM_SETTINGS_UPDATED', 'TREATMENT_PATHWAY_ADDED', 'TREATMENT_PATHWAY_REMOVED'
  ].sort();


  if (isLoading && logs.length === 0) { // Show loading only if no logs are displayed yet
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading activity logs...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2, p:2 }}>
      <Typography variant="h6" component="div" sx={{ mb: 2 }}>
        Admin Activity Log
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>User</InputLabel>
            <Select name="userId" value={filters.userId} label="User" onChange={handleFilterChange}>
              <MenuItem value=""><em>All Users</em></MenuItem>
              {users.map(user => <MenuItem key={user._id} value={user._id}>{user.email} ({user.name})</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>Actor</InputLabel>
            <Select name="actorId" value={filters.actorId} label="Actor" onChange={handleFilterChange}>
              <MenuItem value=""><em>All Actors</em></MenuItem>
              {users.map(user => <MenuItem key={user._id} value={user._id}>{user.email} ({user.name})</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>Event Type</InputLabel>
            <Select name="eventType" value={filters.eventType} label="Event Type" onChange={handleFilterChange}>
              <MenuItem value=""><em>All Types</em></MenuItem>
              {eventTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TextField name="startDate" label="Start Date" type="date" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} fullWidth size="small"/>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TextField name="endDate" label="End Date" type="date" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} fullWidth size="small"/>
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-start" gap={1}>
            <Button variant="outlined" onClick={handleClearFilters}>Clear Filters</Button>
            <Button variant="contained" onClick={() => setRefreshTrigger(prev => prev + 1)}>Refresh</Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{my:2}}>{error}</Alert>}
      
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="activity log table">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
                <TableCell>Event Type</TableCell>
              <TableCell>User (Email)</TableCell>
              <TableCell>Actor (Email)</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && logs.length > 0 && (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No activity logs found for the current filters.
                </TableCell>
              </TableRow>
            )}
            {logs.map((log) => (
              <TableRow hover role="checkbox" tabIndex={-1} key={log._id}>
                <TableCell>
                  {log.timestamp ? format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
                </TableCell>
                <TableCell>{log.eventType}</TableCell>
                <TableCell>
                  {log.userId ? (
                    <>
                      {log.userId.name}
                      <br />
                      <Typography variant="caption" color="textSecondary">({log.userId.email || log.userId._id})</Typography>
                    </>
                  ) : 'N/A'}
                </TableCell>
                <TableCell>
                  {log.actorId ? (
                    <>
                      {log.actorId.name}
                      <br />
                      <Typography variant="caption" color="textSecondary">({log.actorId.email || log.actorId._id})</Typography>
                    </>
                  ) : 'System/N/A'}
                </TableCell>
                <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                <TableCell>{log.location ? `${log.location.city || ''}, ${log.location.country || ''}`.replace(/^, |^$/, '') || 'N/A' : 'N/A'}</TableCell>
                <TableCell>{log.deviceInfo ? `${log.deviceInfo.browser} on ${log.deviceInfo.os}` : 'N/A'}</TableCell>
                <TableCell sx={{maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  <pre style={{margin:0, whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{log.details ? JSON.stringify(log.details, null, 2) : 'N/A'}</pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalLogsCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default ActivityLogViewer;
